import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons'
import MinimumQuantity from '../minimum-quantity/MinimumQuantity'
import QuantityInput from '../quantity-input/QuantityInput'
import Loader from '../loader/Loader'
import { productMeta, productName } from '../menu-item/MenuItem'
import { useGetProductByIdQuery } from '../../store/api/productsApi'
import { useGetIngredientsQuery } from '../../store/api/ingredientsApi'
import { useAppType } from '../../context/AppContext'
import { IProduct } from '../../types/products'
import { IIngredient, ISelectedIngredient } from '../../types/ingredients'
import { imageUrl, imageSrcSet } from '../../utils/imageUrl'
import { handleImageFallback } from '../../utils/brand'
import { discountedPrice } from '../../utils/cart'
import { flyToCart, haptic, iconBurst, prefersReducedMotion } from '../../utils/fx'
import { useDeviceDetect } from '../../utils/use-device-detect'
import { ProductRouteState, takeProductOrigin } from '../../utils/openProduct'
import './ProductModal.scss'

type CartProps = {
  setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
}

const c = 'product-modal'
const DURATION = 460
const EASE = 'cubic-bezier(.22,1,.36,1)'
const DRAG_CLOSE_DISTANCE = 110

function isOnScreen(el: Element | null): el is HTMLElement {
  if (!el || !el.isConnected) return false
  const r = el.getBoundingClientRect()
  return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight
}

function flyImage(from: HTMLElement, to: HTMLElement, src: string) {
  const a = from.getBoundingClientRect()
  const b = to.getBoundingClientRect()
  const clone = document.createElement('img')
  clone.src = src
  clone.setAttribute('aria-hidden', 'true')
  Object.assign(clone.style, {
    position: 'fixed',
    zIndex: '1300',
    margin: '0',
    objectFit: 'cover',
    pointerEvents: 'none',
    left: '0',
    top: '0',
  })
  document.body.appendChild(clone)

  const box = (r: DOMRect, el: HTMLElement) => ({
    transform: `translate(${r.left}px, ${r.top}px)`,
    width: `${r.width}px`,
    height: `${r.height}px`,
    borderRadius: getComputedStyle(el).borderRadius,
  })

  const hidden = [from, to]
  hidden.forEach((el) => (el.style.visibility = 'hidden'))
  const animation = clone.animate([box(a, from), box(b, to)], {
    duration: DURATION,
    easing: EASE,
    fill: 'forwards',
  })
  const done = () => {
    hidden.forEach((el) => (el.style.visibility = ''))
    clone.remove()
  }
  animation.onfinish = done
  animation.oncancel = done
}

function imageOf(el: HTMLElement) {
  const img = el.tagName === 'IMG' ? (el as HTMLImageElement) : el.querySelector('img')
  return img?.currentSrc || img?.src || ''
}

function useScrollLock() {
  useLayoutEffect(() => {
    const html = document.documentElement
    const body = document.body
    const gap = window.innerWidth - html.clientWidth
    const prev = [body.style.overflow, body.style.paddingRight]
    body.style.overflow = 'hidden'
    if (gap > 0) body.style.paddingRight = `${gap}px`
    return () => {
      body.style.overflow = prev[0]
      body.style.paddingRight = prev[1]
    }
  }, [])
}

type SheetProps = CartProps & {
  id: string
  closing: boolean
  onRequestClose: () => void
  onExited: () => void
}

const ProductSheet = ({ id, closing, onRequestClose, onExited, setCart }: SheetProps) => {
  const { t } = useTranslation()
  const { state } = useLocation()
  const appType = useAppType()
  const { isMobile } = useDeviceDetect()
  const [quantity, setQuantity] = useState(1)
  const [added, setAdded] = useState(false)
  const [selectedIngredients, setSelectedIngredients] = useState<ISelectedIngredient[]>([])
  const backdropRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const heroRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const drag = useRef({ startY: 0, startT: 0, y: 0, active: false })
  const flyBack = useRef(true)
  const closeTimer = useRef<number>()

  const { data: fetched, isLoading } = useGetProductByIdQuery(id)
  const { data: allIngredients } = useGetIngredientsQuery()
  const preview = (state as ProductRouteState | null)?.preview
  const [cachedPreview] = useState(preview?._id === id ? preview : undefined)
  const data = fetched ?? cachedPreview

  useScrollLock()

  useLayoutEffect(() => {
    const panel = panelRef.current
    const hero = heroRef.current
    if (!panel || !hero) return
    panel.focus({ preventScroll: true })
    backdropRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: DURATION,
      easing: 'ease-out',
    })
    if (prefersReducedMotion()) {
      panel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200 })
      return
    }
    panel.animate(
      isMobile
        ? [{ transform: 'translateY(100%)' }, { transform: 'none' }]
        : [
            { opacity: 0, transform: 'translateY(28px) scale(.96)' },
            { opacity: 1, transform: 'none' },
          ],
      { duration: DURATION, easing: EASE },
    )
    const source = takeProductOrigin(id)
    const src = source && imageOf(source)
    if (src && isOnScreen(source)) flyImage(source, hero, src)
  }, [])

  useEffect(() => {
    if (!closing) return
    const panel = panelRef.current
    const hero = heroRef.current
    if (!panel || !hero) return onExited()
    const reduce = prefersReducedMotion()
    const duration = reduce ? 160 : DURATION * 0.8

    backdropRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration,
      easing: 'ease-in',
      fill: 'forwards',
    })
    const exit = panel.animate(
      reduce
        ? [{ opacity: 1 }, { opacity: 0 }]
        : isMobile
          ? [{ transform: `translateY(${drag.current.y}px)` }, { transform: 'translateY(100%)' }]
          : [
              { opacity: 1, transform: 'none' },
              { opacity: 0, transform: 'translateY(20px) scale(.96)' },
            ],
      { duration, easing: EASE, fill: 'forwards' },
    )
    exit.onfinish = onExited

    const origin = takeProductOrigin(id)
    const src = imageOf(hero)
    if (!reduce && flyBack.current && src && isOnScreen(origin) && isOnScreen(hero)) {
      flyImage(hero, origin, src)
    }
  }, [closing])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onRequestClose()
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(closeTimer.current)
    }
  }, [onRequestClose])

  useEffect(() => {
    if (!isLoading && !data) onRequestClose()
  }, [isLoading, data])

  function onTouchStart(e: React.TouchEvent) {
    if (!isMobile || closing) return
    const atTop = (scrollRef.current?.scrollTop ?? 0) <= 0
    drag.current = {
      startY: e.touches[0].clientY,
      startT: e.timeStamp,
      y: 0,
      active: atTop,
    }
  }

  function onTouchMove(e: React.TouchEvent) {
    const d = drag.current
    if (!d.active || !panelRef.current) return
    const dy = e.touches[0].clientY - d.startY
    if (dy < 0 && d.y === 0) {
      d.active = false
      return
    }
    d.y = Math.max(0, dy)
    panelRef.current.style.transform = `translateY(${d.y}px)`
  }

  function onTouchEnd(e: React.TouchEvent) {
    const d = drag.current
    const panel = panelRef.current
    if (!d.active || !panel) return
    d.active = false
    const velocity = d.y / Math.max(1, e.timeStamp - d.startT)
    if (d.y > DRAG_CLOSE_DISTANCE || (d.y > 30 && velocity > 0.6)) {
      haptic()
      onRequestClose()
      return
    }
    panel.style.transform = ''
    panel.animate([{ transform: `translateY(${d.y}px)` }, { transform: 'none' }], {
      duration: 320,
      easing: EASE,
    })
    d.y = 0
  }

  const description =
    i18next.language === 'ru' && data?.ru_description ? data.ru_description : data?.description
  const descriptionLines = (description ?? '')
    .split('_')
    .map((line) => line.trim())
    .filter(Boolean)

  const availableIngredients =
    allIngredients?.filter((ing: IIngredient) => data?.availableIngredients?.includes(ing._id)) ||
    []

  const ingredientsTotal = selectedIngredients.reduce((sum, ing) => sum + ing.price, 0)
  const unitPrice = discountedPrice(data) + ingredientsTotal

  function toggleIngredient(ingredient: IIngredient) {
    haptic()
    setSelectedIngredients((prev) =>
      prev.some((i) => i._id === ingredient._id)
        ? prev.filter((i) => i._id !== ingredient._id)
        : [
            ...prev,
            {
              _id: ingredient._id,
              label: ingredient.label,
              label_ru: ingredient.label_ru,
              price: ingredient.customer_price ?? 0,
            },
          ],
    )
  }

  function addToCart(e: React.MouseEvent) {
    if (!data || added) return
    const params = {
      ...data,
      quantity,
      price: String(unitPrice),
      selectedIngredients: selectedIngredients.length > 0 ? selectedIngredients : undefined,
    } as IProduct

    setCart((cart) => {
      const itemIndex = cart.findIndex((cartItem) => cartItem._id === data._id)
      if (itemIndex === -1) return [...cart, params]
      return cart.map((item, i) =>
        i === itemIndex
          ? {
              ...item,
              quantity: (item.quantity ?? 0) + quantity,
              selectedIngredients: params.selectedIngredients,
              price: params.price,
            }
          : item,
      )
    })

    haptic([10, 30, 10])
    iconBurst(e.clientX, e.clientY)
    flyToCart(heroRef.current, imageUrl(data.image, 240) || undefined)
    setAdded(true)
    flyBack.current = false
    closeTimer.current = window.setTimeout(onRequestClose, 700)
  }

  const media = (
    <div
      ref={heroRef}
      className={`${c}__media`}
      style={{
        backgroundImage: data?.image ? `url(${imageUrl(data.image, 480)})` : undefined,
      }}
    >
      {data && (
        <img
          src={imageUrl(data.image, 1024)}
          srcSet={imageSrcSet(data.image, [480, 800, 1024])}
          sizes='(max-width: 768px) 100vw, 460px'
          alt=''
          fetchPriority='high'
          decoding='async'
          onError={handleImageFallback(appType)}
        />
      )}
      <div className={`${c}__badges`}>
        {data?.isNew && <span className={`${c}__badge ${c}__badge--new`}>{t('NEW')}</span>}
        {data?.discount && (
          <span className={`${c}__badge ${c}__badge--sale`}>-{data.discount}%</span>
        )}
      </div>
    </div>
  )

  const content = data ? (
    <div className={`${c}__content`}>
      <h2 className={`${c}__title`}>{productName(data)}</h2>
      {productMeta(data, t('PIECES')) && (
        <div className={`${c}__meta`}>
          {productMeta(data, t('PIECES'))
            .split(' · ')
            .map((item) => (
              <span key={item}>{item}</span>
            ))}
        </div>
      )}
      <MinimumQuantity product={data} />

      {descriptionLines.length > 0 && (
        <ul className={`${c}__description`}>
          {descriptionLines.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      )}

      {availableIngredients.length > 0 && (
        <div className={`${c}__ingredients`}>
          <div className={`${c}__section-title`}>{t('ADDITIONAL_INGREDIENTS')}</div>
          <div className={`${c}__ingredients-list`}>
            {availableIngredients.map((ingredient: IIngredient) => {
              const checked = selectedIngredients.some((i) => i._id === ingredient._id)
              return (
                <button
                  key={ingredient._id}
                  type='button'
                  role='checkbox'
                  aria-checked={checked}
                  className={`${c}__ingredient${checked ? ' is-checked' : ''}`}
                  onClick={() => toggleIngredient(ingredient)}
                >
                  <span className={`${c}__ingredient-check`}>
                    <FontAwesomeIcon icon={checked ? faCheck : faPlus} />
                  </span>
                  {i18next.language === 'ru' && ingredient.label_ru
                    ? ingredient.label_ru
                    : ingredient.label}
                  <small>+{ingredient.customer_price ?? 0} lei</small>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className={`${c}__price`}>
        {data.discount && <s>{data.price} lei</s>}
        <strong key={unitPrice}>{unitPrice} lei</strong>
      </div>
    </div>
  ) : (
    <div className={`${c}__loading`}>
      <Loader />
    </div>
  )

  const bar = (
    <div className={`${c}__bar`}>
      <QuantityInput setQuantity={setQuantity} quantity={quantity} size='lg' />
      <button
        className={`${c}__add${added ? ' is-added' : ''}`}
        onClick={addToCart}
        disabled={!data || !quantity}
      >
        <span className={`${c}__add-label`}>
          <FontAwesomeIcon icon={added ? faCheck : faPlus} />
          {added ? t('SUCCESSFULLY_ADDED') : t(isMobile ? 'ADD' : 'ADD_TO_CART')}
        </span>
        {!added && <span className={`${c}__add-price`}>{unitPrice * quantity} lei</span>}
      </button>
    </div>
  )

  return (
    <div className={`${c}${closing ? ' is-closing' : ''}`}>
      <div ref={backdropRef} className={`${c}__backdrop`} onClick={onRequestClose} />
      <div
        ref={panelRef}
        className={`${c}__panel`}
        role='dialog'
        aria-modal='true'
        aria-label={productName(data)}
        tabIndex={-1}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {isMobile && <span className={`${c}__grabber`} aria-hidden />}
        <button className={`${c}__close`} onClick={onRequestClose} aria-label={t('CLOSE')}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
        {isMobile ? (
          <>
            <div ref={scrollRef} className={`${c}__scroll`}>
              {media}
              {content}
            </div>
            {bar}
          </>
        ) : (
          <>
            {media}
            <div className={`${c}__body`}>
              <div ref={scrollRef} className={`${c}__scroll`}>
                {content}
              </div>
              {bar}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const ProductModal = ({ id, setCart }: CartProps & { id: string | null }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const appType = useAppType()
  const [shownId, setShownId] = useState(id)

  useEffect(() => {
    if (id) setShownId(id)
  }, [id])

  const fromApp = !!(location.state as ProductRouteState | null)?.backgroundLocation
  const requestClose = useCallback(() => {
    if (!id) return
    if (fromApp) navigate(-1)
    else navigate(`/${appType}`, { replace: true })
  }, [id, fromApp, navigate, appType])

  const onExited = useCallback(() => setShownId(null), [])

  const current = id ?? shownId
  if (!current) return null

  return createPortal(
    <ProductSheet
      key={current}
      id={current}
      closing={!id}
      onRequestClose={requestClose}
      onExited={onExited}
      setCart={setCart}
    />,
    document.body,
  )
}

export default ProductModal
